import React, { useState, useEffect } from 'react';
import bottleReturnService from '../../db/bottle-return-service';
import './AdminBottleReturnsDashboard.scss';

const AdminBottleReturnsDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminPassword, setAdminPassword] = useState('');
  const [creditValue, setCreditValue] = useState(6);
  const [showSettings, setShowSettings] = useState(false);
  const [showAddCredits, setShowAddCredits] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [manualCustomerMobile, setManualCustomerMobile] = useState('');
  const [manualCreditAmount, setManualCreditAmount] = useState('');
  const [manualAdminPassword, setManualAdminPassword] = useState('');

  useEffect(() => {
    loadRequests();
    const interval = setInterval(loadRequests, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadRequests = async () => {
    try {
      const data = await bottleReturnService.getPendingRequests();
      setRequests(data || []);
      setError('');
    } catch (error) {
      console.error('Error loading requests:', error);
      setRequests([]);
      // Don't show error if it's just no requests
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    if (!adminPassword) {
      setError('Admin password required');
      return;
    }

    try {
      await bottleReturnService.approveBottleReturn(requestId, adminPassword);
      setMessage('✓ Bottle return approved and credits added');
      setAdminPassword('');
      setSelectedRequest(null);
      await loadRequests();
    } catch (error) {
      setError('Failed to approve request');
    }
  };

  const handleReject = async (requestId, reason) => {
    try {
      await bottleReturnService.rejectBottleReturn(requestId, reason);
      setMessage('✓ Bottle return rejected');
      setSelectedRequest(null);
      await loadRequests();
    } catch (error) {
      setError('Failed to reject request');
    }
  };

  const handleUpdateCreditValue = async () => {
    try {
      await bottleReturnService.updateCreditValue(creditValue);
      setMessage(`✓ Credit value updated to ₹${creditValue} per bottle`);
      setShowSettings(false);
    } catch (error) {
      setError('Failed to update credit value');
    }
  };

  const handleAddManualCredits = async () => {
    if (!manualCustomerMobile || !manualCreditAmount || !manualAdminPassword) {
      setError('All fields required');
      return;
    }

    try {
      setError('');
      setMessage('');
      await bottleReturnService.addManualCredits(
        manualCustomerMobile,
        parseInt(manualCreditAmount),
        manualAdminPassword
      );
      setMessage(`✓ ₹${manualCreditAmount} credits added to customer ${manualCustomerMobile}`);
      setManualCustomerMobile('');
      setManualCreditAmount('');
      setManualAdminPassword('');
      setShowAddCredits(false);
    } catch (error) {
      console.error('Add credits error:', error);
      setError(error.message || 'Failed to add credits');
    }
  };

  if (loading) return <div className="loading">Loading bottle returns...</div>;

  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const totalBottles = requests.reduce((sum, r) => sum + (r.bottleCount || 0), 0);
  const totalCredits = totalBottles * creditValue;

  return (
    <div className="admin-bottle-returns-dashboard">
      <div className="dashboard-header">
        <h2>Bottle Returns Management</h2>
        <div className="header-buttons">
          <button 
            className="add-credits-btn"
            onClick={() => setShowAddCredits(!showAddCredits)}
          >
            ➕ Add Credits
          </button>
          <button 
            className="settings-btn"
            onClick={() => setShowSettings(!showSettings)}
          >
            ⚙️ Settings
          </button>
        </div>
      </div>

      {showAddCredits && (
        <div className="add-credits-panel">
          <h3>Add Manual Credits</h3>
          <div className="form-group">
            <label>Mobile Number</label>
            <input
              type="tel"
              placeholder="Enter mobile number"
              value={manualCustomerMobile}
              onChange={(e) => setManualCustomerMobile(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Credit Amount (₹)</label>
            <input
              type="number"
              min="1"
              placeholder="Enter amount"
              value={manualCreditAmount}
              onChange={(e) => setManualCreditAmount(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Admin Password</label>
            <input
              type="password"
              placeholder="Enter admin password"
              value={manualAdminPassword}
              onChange={(e) => setManualAdminPassword(e.target.value)}
            />
          </div>
          <div className="form-actions">
            <button className="submit-btn" onClick={handleAddManualCredits}>Add Credits</button>
            <button className="cancel-btn" onClick={() => setShowAddCredits(false)}>Cancel</button>
          </div>
        </div>
      )}

      {showSettings && (
        <div className="settings-panel">
          <h3>Bottle Credit Settings</h3>
          <div className="setting-group">
            <label>Credit per Bottle (₹)</label>
            <input
              type="number"
              min="1"
              value={creditValue}
              onChange={(e) => setCreditValue(parseInt(e.target.value))}
            />
            <button onClick={handleUpdateCreditValue}>Update</button>
          </div>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <h4>Pending Requests</h4>
          <p className="stat-value">{pendingCount}</p>
        </div>
        <div className="stat-card">
          <h4>Total Bottles</h4>
          <p className="stat-value">{totalBottles}</p>
        </div>
        <div className="stat-card">
          <h4>Total Credits</h4>
          <p className="stat-value">₹{totalCredits}</p>
        </div>
        <div className="stat-card">
          <h4>Credit per Bottle</h4>
          <p className="stat-value">₹{creditValue}</p>
        </div>
      </div>

      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="requests-list">
        <h3>Pending Requests</h3>
      {requests.length === 0 ? (
          <p className="no-requests">No pending bottle return requests</p>
        ) : (
          <div className="requests-grid">
            {requests.map((request) => (
              <div key={request._id} className="request-card">
                <div className="request-header">
                  <span className="customer-id">{request.customerId}</span>
                  <span className={`status ${request.status}`}>{request.status}</span>
                </div>
                <div className="request-details">
                  <p><strong>Bottles:</strong> {request.bottleCount}</p>
                  <p><strong>Credits:</strong> ₹{request.bottleCount * creditValue}</p>
                  <p><strong>Date:</strong> {new Date(request.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="request-actions">
                  <button
                    className="approve-btn"
                    onClick={() => setSelectedRequest(request._id)}
                  >
                    Approve
                  </button>
                  <button
                    className="reject-btn"
                    onClick={() => handleReject(request._id, 'Damaged bottles')}
                  >
                    Reject
                  </button>
                </div>

                {selectedRequest === request._id && (
                  <div className="approval-form">
                    <input
                      type="password"
                      placeholder="Admin password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                    />
                    <button
                      className="confirm-btn"
                      onClick={() => handleApprove(request._id)}
                    >
                      Confirm Approval
                    </button>
                    <button
                      className="cancel-btn"
                      onClick={() => setSelectedRequest(null)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBottleReturnsDashboard;
